import React from 'react';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Container } from '@/Components/ui/container';
import { Heading, Text } from '@/Components/ui/typography';

const CONTACT_EMAIL = 'privacy@searchjob.ai';
const COMPANY_ADDRESS =
  'Newzenalpha Tech Private Limited, Bhive Workspace, JBR Tech Park, EPIP, Whitefield, Bangalore – 560066, Karnataka, India';

/** A numbered/titled policy section with consistent spacing. */
const Section = ({ label, title, children }) => (
  <section className="space-y-4">
    <Heading level="h5" as="h2" className="text-foreground">
      {label ? <span className="text-primary">{label} — </span> : null}
      {title}
    </Heading>
    <div className="space-y-4">{children}</div>
  </section>
);

/** Body paragraph styled to match the rest of the marketing site. */
const Para = ({ children }) => (
  <Text variant="muted" className="leading-relaxed">
    {children}
  </Text>
);

/** Inline sub-heading inside a section (e.g. "How do you get my consent?"). */
const SubHeading = ({ children }) => (
  <Heading level="h6" as="h3" className="text-foreground !mt-6">
    {children}
  </Heading>
);

const MailLink = () => (
  <a
    href={`mailto:${CONTACT_EMAIL}`}
    className="text-primary font-medium hover:underline"
  >
    {CONTACT_EMAIL}
  </a>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <div className="bg-secondary/20 border-b border-border">
        <Container>
          <div className="py-16 md:py-20 max-w-3xl">
            <Heading level="h1" className="mb-4">
              Privacy Policy
            </Heading>
            <Text variant="muted" size="lg">
              Last updated: June 18, 2026
            </Text>
          </div>
        </Container>
      </div>

      {/* Content */}
      <Container>
        <div className="max-w-3xl py-12 md:py-16 space-y-10">
          <Section label="Section 1" title="What Do We Do With Your Information?">
            <Para>
              When you purchase something from our store, as part of the buying
              and selling process, we collect the personal information you give
              us such as your name, address and email address.
            </Para>
            <Para>
              When you browse our store, we also automatically receive your
              computer's internet protocol (IP) address in order to provide us
              with information that helps us learn about your browser and
              operating system.
            </Para>
            <Para>
              Email marketing (if applicable): With your permission, we may send
              you emails about our store, new products and other updates.
            </Para>
          </Section>

          <Section label="Section 2" title="Consent">
            <SubHeading>How do you get my consent?</SubHeading>
            <Para>
              When you sign up with us, we assume that you have read our terms of
              service and privacy policy and are okay with it.
            </Para>

            <SubHeading>What Personal Information do you collect?</SubHeading>
            <Para>
              We collect following (optional) personal information to provide our
              services:
            </Para>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              <li>Name</li>
              <li>Phone</li>
              <li>Email</li>
            </ul>
            <Para>
              All these information are neither shared nor sold to any third
              party services.
            </Para>

            <SubHeading>How do I withdraw my consent?</SubHeading>
            <Para>
              If after you opt-in, you change your mind, you may withdraw your
              consent for us to contact you, for the continued collection, use or
              disclosure of your information, at anytime, by contacting us at{' '}
              <MailLink /> or mailing us at: {COMPANY_ADDRESS}.
            </Para>
          </Section>

          <Section label="Section 3" title="Disclosure">
            <Para>
              We may disclose your personal information if we are required by law
              to do so or if you violate our Terms of Service.
            </Para>
          </Section>

          <Section label="Section 4" title="Third-Party Services">
            <Para>
              In general, the third-party providers used by us will only collect,
              use and disclose your information to the extent necessary to allow
              them to perform the services they provide to us.
            </Para>
            <Para>
              We send anonymised and vectorised messages exchanged on our
              platform with AI models (when opted) to provide AI processing layer
              on our platform.
            </Para>
            <Para>
              However, certain third-party service providers, such as payment
              gateways and other payment transaction processors, have their own
              privacy policies in respect to the information we are required to
              provide to them for your purchase-related transactions.
            </Para>
            <Para>
              For these providers, we recommend that you read their privacy
              policies so you can understand the manner in which your personal
              information will be handled by these providers.
            </Para>
            <Para>
              In particular, remember that certain providers may be located in or
              have facilities that are located a different jurisdiction than
              either you or us. So if you elect to proceed with a transaction that
              involves the services of a third-party service provider, then your
              information may become subject to the laws of the jurisdiction(s) in
              which that service provider or its facilities are located.
            </Para>
            <Para>
              Once you leave our store's website or are redirected to a
              third-party website or application, you are no longer governed by
              this Privacy Policy or our website's Terms of Service.
            </Para>

            <SubHeading>Links</SubHeading>
            <Para>
              When you click on links on our store, they may direct you away from
              our site. We are not responsible for the privacy practices of other
              sites and encourage you to read their privacy statements.
            </Para>
          </Section>

          <Section label="Section 5" title="Security">
            <Para>
              To protect your personal information, we take reasonable
              precautions and follow industry best practices to make sure it is
              not inappropriately lost, misused, accessed, disclosed, altered or
              destroyed.
            </Para>
          </Section>

          <Section label="Section 6" title="Cookies">
            <Para>
              We use cookies to maintain session of your user. It is not used to
              personally identify you on other websites.
            </Para>
          </Section>

          <Section label="Section 7" title="Age of Consent">
            <Para>
              By using this site, you represent that you are at least the age of
              majority in your state or province of residence, or that you are the
              age of majority in your state or province of residence and you have
              given us your consent to allow any of your minor dependents to use
              this site.
            </Para>
          </Section>

          <Section label="Section 8" title="Changes to This Privacy Policy">
            <Para>
              We reserve the right to modify this privacy policy at any time, so
              please review it frequently. Changes and clarifications will take
              effect immediately upon their posting on the website. If we make
              material changes to this policy, we will notify you here that it has
              been updated, so that you are aware of what information we collect,
              how we use it, and under what circumstances, if any, we use and/or
              disclose it.
            </Para>
            <Para>
              If our store is acquired or merged with another company, your
              information may be transferred to the new owners so that we may
              continue to sell products to you.
            </Para>
          </Section>

          <Section title="Questions and Contact Information">
            <Para>
              If you would like to: access, correct, amend or delete any personal
              information we have about you, register a complaint, or simply want
              more information contact our Privacy Compliance Officer at{' '}
              <MailLink /> or mailing us at:
            </Para>
            <address className="not-italic text-muted-foreground leading-relaxed">
              Privacy Compliance Officer
              <br />
              Newzenalpha Tech Private Limited,
              <br />
              Bhive Workspace, JBR Tech Park,
              <br />
              EPIP, Whitefield, Bangalore – 560066,
              <br />
              Karnataka, India
            </address>
          </Section>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
